import { useState } from 'react';
import { LogOut } from 'lucide-react';
import { cn } from '../../lib/utils';
import { getMenuList } from '../../lib/menu-list';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider
} from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { logoutUser } from '@/lib/directus';
import { useNavigate } from 'react-router-dom';

interface MenuProps {
  isOpen: boolean | undefined;
}

function Menu({ isOpen }: MenuProps) {
  const [pathname] = useState<string>(window.location.pathname);
  const menuList = getMenuList(pathname);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigation = useNavigate();

  const handleLogout = () => {
    logoutUser();
    navigation('/login')
    localStorage.removeItem('directus_auth')

    setIsModalOpen(false);
  };

  return (
    <ScrollArea className="[&gt;div&gt;div[style]]:!block">
      <nav className={cn(isOpen === false ? 'mt-8 h-full w-2/6' : 'mt-8 h-full ')}>
        <ul className="flex flex-col min-h-[calc(100vh-48px-36px-16px-32px)] lg:min-h-[calc(100vh-32px-40px-32px)] items-start space-y-1 px-2">
          {menuList.map(({ href, label, icon: Icon, active }, index) => (
            <li className="w-full" key={index}>
              <TooltipProvider disableHoverableContent>
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <Button
                      variant={active ? 'secondary' : 'ghost'}
                      className="w-full justify-start h-10 mb-1"
                      asChild
                    >
                      <a href={href}>
                        <span className={cn(isOpen === false ? '' : 'mr-4')}>
                          <Icon size={18} />
                        </span>
                        <p
                          className={cn(
                            ' min-w-[125px] max-w-[200px] truncate',
                            isOpen === false
                              ? '-translate-x-96 opacity-0'
                              : 'translate-x-0 opacity-100'
                          )}
                        >
                          {label}
                        </p>
                      </a>
                    </Button>
                  </TooltipTrigger>
                  {isOpen === false && (
                    <TooltipContent side="right">{label}</TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            </li>
          ))}
          <li className="w-full grow flex items-end">
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>

              <DialogTrigger asChild>
                <Button
                  onClick={() => setIsModalOpen(true)} // Ouvre le modal de confirmation
                  variant="outline"
                  className={cn(isOpen === false ? 'w-2/5 justify-center h-10 mt-5 ' : 'w-full justify-center h-10 mt-5')}
                >
                  <span className={cn(isOpen === false ? '' : 'mr-4')}>
                    <LogOut size={18} />
                  </span>
                  <p
                    className={cn(
                      'whitespace-nowrap',
                      isOpen === false ? 'opacity-0 hidden' : 'opacity-100'
                    )}
                  >
                    Déconnexion
                  </p>
                </Button>
              </DialogTrigger>
              <DialogContent className="w-[300px] p-6">
                <DialogHeader>
                  <DialogTitle>Confirmation</DialogTitle>
                  <DialogDescription>
                    Êtes-vous sûr de vouloir vous déconnecter ?
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsModalOpen(false)}>Annuler</Button>
                  <Button variant="destructive" onClick={handleLogout}>Confirmer</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </li>
        </ul>
      </nav>
    </ScrollArea>
  );
}

export default Menu;
